const cities = [
  { name: "Jensen Beach", zips: "34957" },
  { name: "Stuart", zips: "34994, 34996" },
  { name: "Palm City", zips: "34990, 34991" },
  { name: "Hobe Sound", zips: "33455" },
  { name: "Port Salerno", zips: "34992" },
  { name: "Sewall's Point", zips: "34996" },
  { name: "Hutchinson Island", zips: "34957" },
  { name: "Indiantown", zips: "34956" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or the boat in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function JensenBeachLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] pt-6 md:pt-8 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Jensen Beach &amp; Coastal Martin County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Jensen Beach, Stuart, Palm City, or anywhere on Hutchinson Island and the Treasure Coast, Fast Passport Center makes expedited passport processing simple — no drive to Miami, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Jensen Beach is not like the other Treasure Coast communities. While Stuart is the county seat and Palm City is the suburban corridor, Jensen Beach occupies a unique position as the beach town that time forgot — or rather, the beach town that chose not to forget itself. Located on the Indian River Lagoon side of Martin County, with the barrier island beaches of Hutchinson Island just across the Jensen Beach Causeway, this community of roughly 13,000 residents has preserved an Old Florida authenticity that most coastal towns surrendered decades ago to high-rise development and tourist strip commercialization.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Jensen Beach's identity is deeply rooted in its pineapple heritage. In the late 19th century, the area was known as the "Pineapple Capital of the World," producing more pineapples than any other region in the United States. The Pineapple Festival, held annually in November, celebrates this agricultural legacy with live music, local vendors, and that small-town community gathering that defines Jensen Beach's culture. The historic downtown district along Jensen Beach Boulevard retains the character of a Florida fishing village from another era — low-rise buildings, locally owned shops, a genuine antique district, and restaurants where the owner remembers your name.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What makes Jensen Beach truly distinctive is the combination of barrier island beach access and Indian River Lagoon living. Residents can launch a boat from the Indian River side, cruise through the St. Lucie Inlet, and be in the Atlantic in minutes. The St. Lucie Inlet Preserve State Park, located at the southern tip of Hutchinson Island, protects one of the most ecologically significant estuary systems on Florida's east coast. The Elliott Museum on Hutchinson Island houses an extraordinary collection of antique automobiles and local maritime history. Indian Riverside Park, on the mainland side, offers waterfront recreation, a fishing pier, and the Mansion at Tuckahoe — a beautifully restored 1930s estate that hosts weddings and events overlooking the Indian River.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Jensen Beach's travel demographics are shaped by its unique position. The fishing and boating community — charter captains, commercial fishermen, recreational anglers, and marine service professionals — travels to the Bahamas for deep-sea fishing tournaments, to Costa Rica for Pacific billfish, and to Mexico for sportfishing excursions. These trips are often planned around tournament schedules, weather windows, and seasonal fish migration patterns. Snowbirds who own Jensen Beach properties as their winter escape may discover passport issues when transitioning from their northern homes to international travel plans. Families who come for the beaches and stay for the lifestyle book Caribbean cruises and spring break trips that require current passports for every member.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The geographic reality matters for Jensen Beach residents. The closest regional passport agency is in Miami, approximately 110 miles south on the Florida Turnpike or I-95 — a drive that consumes nearly two hours even in good traffic. For a Jensen Beach charter captain who discovers an expired passport a week before a Bahamas fishing tournament, that drive is not just impractical — it may be impossible given charter schedules and boat preparation demands. Palm Beach International Airport (PBI) is 45 miles south, and Melbourne Orlando International (MLB) is 40 miles north, but both are domestic-focused hubs that require connecting flights for most international destinations.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Jensen Beach travelers across every demographic the community serves. We understand that a charter captain flying to the Bahamas for a marlin tournament has the same urgency as a family of four leaving on a Caribbean cruise from Port Everglades. Our courier-based expedited service means you start online from your Jensen Beach home, your waterfront condo, or your favorite downtown café — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Port St. Lucie</p>
                  <p className="text-white/60 text-xs mt-0.5">1660 St Lucie W Blvd, Port St. Lucie, FL 34986</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Jensen Beach — approximately 15 miles north via US-1 / I-95
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Martin County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or waterfront property in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location in Stuart or Jensen Beach, or <strong className="text-white">schedule a drop-off</strong> at our Port St. Lucie office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Martin and northern St. Lucie County — start online, ship overnight, and we handle the rest. No need to drive to Miami.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Stuart", href: "/stuart" },
                  { name: "Port St. Lucie", href: "/port-st-lucie" },
                  { name: "Jupiter", href: "/jupiter" },
                  { name: "Palm Beach Gardens", href: "/palm-beach-gardens" },
                  { name: "West Palm Beach", href: "/west-palm-beach" },
                  { name: "Wellington", href: "/wellington" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}